import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-app-launcher>` says about itself.
 *
 * Two things a reader would otherwise have to work out from the source.
 *
 * WHAT IT IS. The visible part is a waffle button and nothing else — no app name, no mark. The
 * active app is represented by its TABS in the row beside it, so the button carries the current app
 * only in its accessible name and its tooltip. Everything else about the piece is the panel it
 * opens, which is portaled to `document.body` under its own shadow host so the tab row's sideways
 * overflow can never clip it.
 *
 * ITS PANEL DOES NOT READ THE RADIUS OR SHADOW TOKENS. The panel's corner is a literal `4px` and
 * its shadow a literal `0 18px 48px rgba(0,0,0,0.42)`, where the sibling popup at the other end of
 * the same row reads `--caos-radius` and `--caos-shadow-float`. Those two literals are absent from
 * the token table below, because that table is what somebody restyling the platform reads and
 * listing a token this piece does not honour would be worse than the gap it records.
 */
const docs: ComponentDocs = {
  aliases: ['waffle', 'app menu', 'app switcher', 'app picker', 'nine-dot menu', 'grid menu'],

  answers: [
    'let somebody move to a different app',
    'show every app this person may open',
    'find an object without knowing which app holds it',
    'show which app is currently open',
  ],

  // The frame's own leading control. It appears once, at the start of the workspace tab row, and
  // nowhere else — a launcher dropped on a record page would be a second way to leave the app from
  // inside that app's own content.
  placements: ['seam'],

  inputs: [
    {
      name: 'apps',
      // No `attribute`: a list of apps cannot ride an attribute. It is set as a property.
      type: 'json',
      description:
        'The apps to list, as `{ key, label, icon?, brand?, destinationCount, children? }`. `icon` names ' +
        'a glyph from the shared vocabulary and is DRAWN; a name outside the vocabulary is printed as raw ' +
        'text instead, so an app from somebody else’s package is never blank. `children` are the app’s ' +
        'objects, listed indented beneath it. Left unset, the panel reads “No apps yet.”',
    },
    {
      name: 'activeAppKey',
      attribute: 'active-app-key',
      type: 'text',
      description:
        'The `key` of the app currently open. Its row in the panel takes the accent bar, the tinted fill ' +
        'and the CURRENT marker, and the button’s accessible name names it. Unset ⇒ no row is marked and ' +
        'the button is just “App Launcher”.',
    },
  ],

  events: [
    {
      name: 'launcher-navigate',
      when:
        'An app row, or one of an app’s indented object rows, was chosen. The panel closes itself; going ' +
        'anywhere is the host’s half.',
      carries: '`{ key }` for an app; `{ key, href }` for one of its objects.',
    },
    {
      name: 'launcher-get-more-apps',
      when:
        'The persistent “Get more apps” row at the foot of the panel was chosen. The panel closes ' +
        'itself; what opens is the host’s half — the component knows nothing about packages or installs.',
      carries: 'Nothing. The choice is the whole message.',
    },
  ],

  // No `<slot>` is rendered: the trigger is built by the component and the panel lives in a portal.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-accent', controls: 'The waffle glyph, and the current app row’s inset bar, icon and CURRENT marker.' },
    { token: '--caos-color-border', controls: 'The trigger’s outline, the panel’s outline, and the rules inside the panel.' },
    { token: '--caos-color-border-strong', controls: 'The trigger’s outline while the pointer is over it.' },
    { token: '--caos-color-surface', controls: 'The panel’s background, and the trigger’s fill on hover.' },
    { token: '--caos-color-surface-2', controls: 'The trigger’s resting fill, a row’s hover fill, and the current app row’s tint.' },
    { token: '--caos-color-bg', controls: 'The fill of the search box inside the panel.' },
    { token: '--caos-color-text', controls: 'The app labels, and what is typed into the search box.' },
    { token: '--caos-color-text-muted', controls: 'The section label, an app’s glyph, the child bullets, and the no-matches line.' },
    { token: '--caos-color-text-ghost', controls: 'The search box’s placeholder.' },
    { token: '--caos-focus-ring', controls: 'The ring on the trigger and on a row reached by keyboard.' },
    { token: '--caos-font', controls: 'The typeface inside the panel.' },
    { token: '--caos-font-mono', controls: 'The “Apps” section label and the CURRENT marker.' },
    { token: '--caos-radius-sm', controls: 'The trigger’s corner radius.' },
    { token: '--caos-space-5', controls: 'The inset between the start of the tab row and the trigger.' },
  ],

  // No named seams. Nothing in the shadow tree carries a `part`, so the waffle and the panel it opens
  // cannot be reached from an outside stylesheet.
  parts: [],

  methods: [],

  placing:
    'It is projected into the workspace tab row’s `launcher` slot, where it stays pinned while the tab set ' +
    'scrolls past it. Nothing else places it and nothing sits around it — the shell owns one, hands it the ' +
    'app list this person may see, and listens for where they chose to go. Its panel is portaled to the ' +
    'page, so whatever holds the trigger cannot clip it.',

  whenToUse:
    'Only as the workspace frame’s app switcher. If what you want is a menu of choices somewhere on a page, ' +
    'this is not it — this piece is the one place a person leaves the app they are in.',

  insteadUse: [
    { name: 'WorkspaceTabs', when: 'Somebody is moving between destinations inside the app they are already in.' },
    { name: 'NavGadget', when: 'Somebody wants to change which tabs their own row shows, rather than move to another app.' },
    { name: 'OverflowMenu', when: 'What is behind the trigger is a list of ACTIONS on a record or a row, not a list of places.' },
  ],

  accessibility:
    'The trigger is a real button carrying `aria-haspopup="menu"` and an `aria-expanded` kept in step with ' +
    'the panel, and its accessible name names the current app. The panel is a `role="menu"` labelled “Apps” ' +
    'whose rows are `role="menuitem"`, the current app’s row is marked `aria-current`, the search box is ' +
    'labelled, and the glyphs are hidden from assistive technology. Escape and a click outside both dismiss ' +
    'it, and focus moves to the search box when it opens. While it is open, Tab and Shift+Tab stay inside ' +
    'the panel and wrap at either end; Escape or choosing an app hands focus back to the trigger, and a ' +
    'click outside leaves focus where the click put it. What is left to the author: an app’s LABEL is the ' +
    'only thing telling one row from another.',

  examples: [
    {
      key: 'apps',
      label: 'The app list',
      summary:
        'The button as it sits at the head of the tab row; pressing it opens the real panel, with the current ' +
        'app marked and its objects listed beneath it.',
      placement: {
        component: 'caos-app-launcher',
        inputs: {
          activeAppKey: 'sales-atlas',
          apps: [
            {
              key: 'sales-atlas',
              label: 'Sales Atlas',
              icon: 'building-2',
              destinationCount: 3,
              children: [
                { key: 'account', label: 'Accounts', href: '/sales-atlas/account' },
                { key: 'contact', label: 'Contacts', href: '/sales-atlas/contact' },
                { key: 'order', label: 'Orders', href: '/sales-atlas/order' },
              ],
            },
            { key: 'setup', label: 'Setup', icon: 'settings', destinationCount: 12 },
            { key: 'atlantis-ai', label: 'Atlantis AI', icon: 'sparkles', destinationCount: 1 },
          ],
        },
      },
      // The launcher IS its panel — a small button until you press it. The stage keeps the room the
      // panel takes, so the app grid opens over its own stage rather than over the page below.
      stage: { opensBelow: 304 }, // measured: 295px panel + 8px gap
    },
  ],
};

export default docs;
